export type FetchLikeError = {
    status?: number;
    statusText?: string;
    url?: string;
};

interface LogEntry {
    level: 'info' | 'warn' | 'error' | 'debug';
    message: string;
    context?: string;
    data?: unknown;
    timestamp?: string;
}

function getStringProperty(data: object, property: string): string | undefined {
    const value = (data as Record<string, unknown>)[property];

    return typeof value === 'string' ? value : undefined;
}

function isErrorLikeObject(data: object): boolean {
    const name = getStringProperty(data, 'name');
    const message = getStringProperty(data, 'message');
    const stack = getStringProperty(data, 'stack');

    return Boolean(stack) || (Boolean(name) && Boolean(message));
}

function extractErrorLikeFields(data: object): Record<string, unknown> {
    const name = getStringProperty(data, 'name');
    const message = getStringProperty(data, 'message');
    const stack = getStringProperty(data, 'stack');

    return {
        ...(name ? { name } : {}),
        ...(message ? { message } : {}),
        ...(stack ? { stack } : {}),
        ...('cause' in data
            ? { cause: normalizeLogData((data as { cause?: unknown }).cause) }
            : {}),
    };
}

function normalizeErrorLikeData(data: object): Record<string, unknown> | null {
    if (!isErrorLikeObject(data)) {
        return null;
    }

    const enumerableNormalized = Object.fromEntries(
        Object.entries(data).map(([key, value]) => [
            key,
            normalizeLogData(value),
        ])
    );

    return {
        ...enumerableNormalized,
        ...extractErrorLikeFields(data),
    };
}

function normalizeLogData(data: unknown): unknown {
    if (data instanceof Error) {
        return {
            name: data.name,
            message: data.message,
            stack: data.stack,
            ...(data.cause !== undefined
                ? { cause: normalizeLogData(data.cause) }
                : {}),
        };
    }

    if (Array.isArray(data)) {
        return data.map(normalizeLogData);
    }

    if (data !== null && typeof data === 'object') {
        const errorLikeData = normalizeErrorLikeData(data);

        if (errorLikeData) {
            return errorLikeData;
        }

        return Object.fromEntries(
            Object.entries(data).map(([key, value]) => [
                key,
                normalizeLogData(value),
            ])
        );
    }

    return data;
}

class Logger {
    private formatLog(entry: LogEntry): string {
        const timestamp = entry.timestamp || new Date().toISOString();
        const logData = {
            time: timestamp,
            level: entry.level.toUpperCase(),
            message: entry.message,
            ...(entry.context ? { context: entry.context } : {}),
            ...(entry.data !== undefined
                ? { data: normalizeLogData(entry.data) }
                : {}),
        };

        // Always use structured JSON logging for consistency
        return JSON.stringify(logData);
    }

    private output(entry: LogEntry): void {
        const formattedLog = this.formatLog(entry);

        // Force output directly to stdout/stderr to bypass Next.js suppression
        try {
            switch (entry.level) {
                case 'error':
                    console.error(formattedLog);
                    break;
                case 'warn':
                    console.warn(formattedLog);
                    break;
                case 'info':
                case 'debug':
                default:
                    console.log(formattedLog);
                    break;
            }
        } catch (err) {
            console.error('Logger failed:', err);
            console.log(formattedLog);
        }
    }

    info(message: string, context?: string, data?: unknown): void {
        this.output({ level: 'info', message, context, data });
    }

    warn(message: string, context?: string, data?: unknown): void {
        this.output({ level: 'warn', message, context, data });
    }

    error(message: string, context?: string, data?: unknown): void {
        this.output({ level: 'error', message, context, data });
    }

    debug(message: string, context?: string, data?: unknown): void {
        // Only log debug in development or if DEBUG env var is set to 'true'
        if (
            typeof process !== 'undefined' &&
            (process.env.NODE_ENV === 'development' ||
                process.env.DEBUG === 'true')
        ) {
            this.output({ level: 'debug', message, context, data });
        }
    }
}

export const logger = new Logger();

/**
 * Logs a failed API call with structured context. Adapted for fetch-based clients
 * (no axios dependency). 4xx responses are logged as warnings; 5xx and network
 * errors are logged as errors.
 */
export function reportApiError(
    error: unknown,
    service: string,
    meta?: { method?: string; url?: string }
): void {
    const method = (meta?.method ?? 'GET').toUpperCase();
    const url = (meta?.url ?? '').split('?')[0];

    const isFetchLike = (e: unknown): e is FetchLikeError =>
        typeof e === 'object' &&
        e !== null &&
        ('status' in e || 'statusText' in e);

    if (isFetchLike(error)) {
        const status = error.status;
        const data = {
            service,
            http_method: method,
            http_url: url || error.url,
            http_status: status != null ? String(status) : 'no_response',
            http_status_text: error.statusText,
        };
        const effectiveUrl = url || (error.url ?? '');
        const message = `API ${method} ${effectiveUrl} failed (${data.http_status})`;

        if (status != null && status >= 400 && status < 500) {
            logger.warn(message, service, data);
        } else {
            logger.error(message, service, data);
        }
        return;
    }

    const message =
        error instanceof Error ? error.message : 'API request failed';
    const errorData =
        error instanceof Error
            ? (normalizeLogData(error) as Record<string, unknown>)
            : { error };

    logger.error(message, service, {
        service,
        http_method: method,
        http_url: url,
        ...errorData,
    });
}

/**
 * Logs non-OK fetch responses. fetch() resolves for HTTP errors; only network
 * failures throw.
 */
export function reportFetchResponseIfError(
    response: Response,
    service: string,
    meta?: { method?: string; url?: string }
): void {
    if (response.ok) {
        return;
    }

    reportApiError(
        {
            status: response.status,
            statusText: response.statusText,
            url: meta?.url ?? response.url,
        },
        service,
        meta
    );
}
